// The documentation that ships inside the admin SPA.
//
// Source of truth is the vision-docs repository: `docs/handbook/*.md` (the
// technical handbook, ordered by `docs/handbook/manifest.json`),
// `docs/overview/*.md` (the executive overview) and `docs/api/openapi/*.json`
// (the OpenAPI descriptions, generated by `tools/docs/build_openapi.py`).
// `scripts/sync-docs.mjs` (predev / prebuild) copies them into `web/docs/`
// (git-ignored) from $VISION_DOCS_DIR or the sibling ../vision-docs; Vite
// inlines them at build time, so the SPA needs no extra endpoint and the pages
// work offline.
import manifest from "../../docs/handbook/manifest.json";

const handbookRaw = import.meta.glob("../../docs/handbook/*.md", { query: "?raw", import: "default", eager: true });
const overviewRaw = import.meta.glob("../../docs/overview/*.md", { query: "?raw", import: "default", eager: true });
const imageUrls = {
  ...import.meta.glob("../../docs/handbook/img/*", { query: "?url", import: "default", eager: true }),
  ...import.meta.glob("../../docs/tasks/img/*", { query: "?url", import: "default", eager: true }),
};
const specs = import.meta.glob("../../docs/api/openapi/*.json", { import: "default", eager: true });

function byName(map) {
  const out = {};
  for (const [path, value] of Object.entries(map)) out[path.split("/").pop()] = value;
  return out;
}

const handbookFiles = byName(handbookRaw);
const overviewFiles = byName(overviewRaw);
const images = byName(imageUrls);

/** Resolve an image path used inside a chapter (`img/x.png`, `../tasks/img/x.png`) to its bundled URL. */
export function resolveImage(src) {
  if (!src || /^(https?:|data:)/.test(src)) return src;
  const name = src.split("/").pop();
  return images[name] || src;
}

function titleOf(markdown, fallback) {
  const m = /^#\s+(.+)$/m.exec(markdown || "");
  return m ? m[1].trim() : fallback;
}

/** Ordered documents: the executive overview first, then the handbook chapters. */
export const DOCUMENTS = [
  ...Object.entries(overviewFiles).map(([file, text]) => ({
    id: file.replace(/\.md$/, "").toLowerCase(),
    group: "Overview",
    title: titleOf(text, file),
    file,
    text,
  })),
  ...manifest.chapters
    .filter((c) => handbookFiles[c.file])
    .map((c) => ({
      id: c.file.replace(/\.md$/, "").toLowerCase(),
      group: "Technical handbook",
      title: c.title || titleOf(handbookFiles[c.file], c.file),
      file: c.file,
      text: handbookFiles[c.file],
      part: c.part,
    })),
];

export const HANDBOOK_VERSION = manifest.version;
export const HANDBOOK_UPDATED = manifest.updated;

/** OpenAPI documents for the API reference page. */
export const API_SPECS = Object.entries(byName(specs))
  .filter(([file]) => file !== "overlay.json")
  .map(([file, spec]) => ({ id: file.replace(/\.json$/, ""), title: spec?.info?.title || file, spec }))
  .sort((a, b) => (a.id === "vision-backend" ? -1 : b.id === "vision-backend" ? 1 : a.title.localeCompare(b.title)));
